import { cn } from "@/lib/utils";

/** Shared corner radii for form controls and related surfaces. */
export const fieldRadius = {
  control: "rounded-lg",
  surface: "rounded-xl",
  popover: "rounded-xl",
  pill: "rounded-full",
  inner: "rounded-md",
} as const;

export type FieldControlSize = "sm" | "md" | "lg";

const CONTROL_HEIGHT: Record<FieldControlSize, string> = {
  sm: "h-8",
  md: "h-9",
  lg: "h-10",
};

const CONTROL_MIN_HEIGHT: Record<FieldControlSize, string> = {
  sm: "min-h-8",
  md: "min-h-9",
  lg: "min-h-10",
};

const CONTROL_PADDING_X: Record<FieldControlSize, string> = {
  sm: "px-2.5",
  md: "px-3",
  lg: "px-3.5",
};

/** Keyboard/focus ring for text-like controls (never `focus:` — mouse click must not ring). */
export const fieldFocusVisibleClass =
  "focus-visible:border-primary-500 focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-primary-500/15";

/** Focus ring for composite shells that wrap an inner trigger (MultiSelect, etc.). */
export const fieldFocusWithinClass =
  "focus-within:border-primary-500 focus-within:outline-none focus-within:ring-[3px] focus-within:ring-primary-500/15";

/** Error border/ring driven by `aria-invalid`, so manual aria overrides still style. */
export const fieldInvalidClass =
  "aria-invalid:border-error-500 aria-invalid:focus-visible:border-error-500 aria-invalid:focus-visible:ring-error-500/15";

export const fieldInvalidWithinClass =
  "aria-invalid:border-error-500 aria-invalid:focus-within:border-error-500 aria-invalid:focus-within:ring-error-500/15";

export const fieldDisabledClass =
  "disabled:cursor-not-allowed disabled:bg-surface-50 disabled:text-surface-500 disabled:shadow-none";

/**
 * Typed-text size for editable controls: 16px below `sm` so iOS Safari does not
 * zoom the page on focus, 14px from `sm` up.
 */
export const fieldTextClass = "text-base sm:text-sm";

const FIELD_CONTROL_BASE = cn(
  "w-full border border-surface-200 bg-surface-0 text-surface-900 shadow-xs transition-[border-color,box-shadow] duration-150 hover:border-surface-300 dark:bg-surface-50/60",
  fieldTextClass,
  "placeholder:text-surface-400",
  fieldRadius.control,
  fieldFocusVisibleClass,
  fieldInvalidClass,
  fieldDisabledClass,
);

/**
 * Single-line form control surface (Input, Select, SearchField, PhoneInput, …).
 * Default `md` is h-9 so it lines up with `Button size="md"`.
 */
export function fieldControlClass({
  size = "md",
  className,
}: {
  size?: FieldControlSize;
  className?: string;
} = {}): string {
  return cn(
    FIELD_CONTROL_BASE,
    CONTROL_HEIGHT[size],
    CONTROL_PADDING_X[size],
    className,
  );
}

/** Multi-line text control surface (Textarea): same chrome as single-line fields. */
export function fieldTextareaClass(className?: string): string {
  return cn(FIELD_CONTROL_BASE, "min-h-9 resize-none px-3 py-2", className);
}

/**
 * Composite control chrome (MultiSelect shell, country triggers) that may grow
 * with chips — uses min-height matching the same size scale.
 */
export function fieldShellClass({
  size = "md",
  className,
}: {
  size?: FieldControlSize;
  className?: string;
} = {}): string {
  return cn(
    "flex w-full items-center overflow-hidden border border-surface-200 bg-surface-0 text-left text-sm text-surface-900 shadow-xs transition-[border-color,box-shadow] duration-150 hover:border-surface-300 dark:bg-surface-50/60",
    fieldRadius.control,
    CONTROL_MIN_HEIGHT[size],
    fieldFocusWithinClass,
    fieldInvalidWithinClass,
    className,
  );
}

/** Popover / listbox panel attached to a field control. */
export function fieldPopoverClass(className?: string): string {
  return cn(
    "border border-surface-200 bg-surface-0 shadow-popover",
    fieldRadius.popover,
    className,
  );
}
